/** Medición de OpenAI Ads. La visita o salida a WhatsApp no prueba una reserva. */
const PIXEL_ID = process.env.NEXT_PUBLIC_OPENAI_ADS_PIXEL_ID || 'K5mDXci2N3TL3CdERpBcTR';
type Pixel = ((...args: unknown[]) => void) & { q?: unknown[][] };
type State = { initialized: boolean; events: Set<string>; session: string; page?: string };
type PixelWindow = Window & { oaiq?: Pixel; __pacOpenAiAds?: State };

function allowed(): boolean {
  if (typeof window === 'undefined') return false;
  if (!['www.pacartagena.com', 'pacartagena.com'].includes(window.location.hostname)) return false;
  if (window.location.pathname.startsWith('/admin')) return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('ph_internal') === '1') return false;
  try { if (window.localStorage.getItem('ph_internal') === '1') return false; } catch { /* almacenamiento opcional */ }
  return true;
}

function pixel(): { queue: Pixel; state: State } | null {
  if (!allowed()) return null;
  const w = window as PixelWindow;
  if (!w.__pacOpenAiAds) {
    let session: string = window.crypto.randomUUID();
    try {
      session = window.sessionStorage.getItem('oai_session') || session;
      window.sessionStorage.setItem('oai_session', session);
    } catch { /* conservar identidad de esta página */ }
    w.__pacOpenAiAds = { initialized: false, events: new Set(), session };
  }
  if (!w.oaiq) {
    const queue: Pixel = (...args) => { queue.q!.push(args); };
    queue.q = [];
    w.oaiq = queue;
  }
  if (!w.__pacOpenAiAds.initialized) {
    w.oaiq('init', { pixelId: PIXEL_ID });
    w.__pacOpenAiAds.initialized = true;
  }
  return { queue: w.oaiq, state: w.__pacOpenAiAds };
}

/** Cola inmediata; el SDK captura oppref y conserva su cookie según el contrato oficial. */
export function initializeOpenAiAds(): void {
  try {
    if (!pixel() || document.getElementById('openai-ads-sdk')) return;
    const script = document.createElement('script');
    script.id = 'openai-ads-sdk';
    script.async = true;
    script.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
    // Un fallo de red permite volver a intentar en la próxima navegación o conversión.
    script.onerror = () => script.remove();
    document.head.appendChild(script);
  } catch { /* la medición nunca bloquea la interfaz */ }
}

function measure(name: string, data: Record<string, unknown>, id: string): boolean {
  try {
    const current = pixel();
    if (!current || current.state.events.has(id)) return false;
    initializeOpenAiAds();
    current.queue('measure', name, data, { event_id: id, opt_out: true });
    // Solo dedup en memoria. Tras recargar se reenvía el mismo ID: el proveedor
    // deduplica y una cola perdida por adblock/red no queda marcada para siempre.
    current.state.events.add(id);
    return true;
  } catch { return false; }
}

export function trackOpenAiPage(pathname: string): void {
  try {
    const current = pixel();
    if (!current || current.state.page === pathname) return;
    if (measure('page_viewed', { type: 'contents' }, `pac-page-${window.crypto.randomUUID()}`)) {
      current.state.page = pathname;
    }
  } catch { /* no bloquear navegación */ }
}

/** Proxy: salida a WhatsApp con fechas y personas; NO mensaje recibido por el bot. */
export function fireOpenAiQualifiedLead(context: {
  kind: 'tour' | 'apartment'; date: string; departure?: string; guests: number; product?: string;
}): boolean {
  if (!context.date || !(context.guests >= 1)) return false;
  if (context.kind === 'apartment' && !context.departure) return false;
  try {
    const current = pixel();
    if (!current) return false;
    const id = ['pac-lead', current.state.session, context.kind, context.product || '',
      context.date, context.departure || '', context.guests].join(':');
    return measure('lead_created', { type: 'customer_action' }, id);
  } catch { return false; }
}

/** Llamar únicamente después de una reserva confirmada por el servidor. Sin importes del redirect. */
export function fireOpenAiConfirmedOrder(orderId: string | null | undefined): boolean {
  if (!orderId?.trim()) return false;
  return measure('order_created', { type: 'contents' }, `pac-order-${orderId}`);
}
